import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Icon from '../../components/AppIcon';
import {
  gastosService,
  gastosRecurrentesService,
  centrosCosteService,
  calcularBaseDesdeTotal,
  calcularIvaDesdeTotal,
} from '../../services/financialService';
import { supabase } from '../../lib/supabase';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,  } from 'recharts';
import OCRGastosWizard from './components/OCRGastosWizard';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const PERIODICIDADES = ['MENSUAL', 'BIMESTRAL', 'TRIMESTRAL', 'SEMESTRAL', 'ANUAL', 'PERSONALIZADA'];
const IVA_OPTIONS = [0, 4, 10, 21];
const CATEGORIAS = [
  'Todos','ALQUILER','ELECTRICIDAD','AGUA','GESTORIA','SEGUROS','TELEFONIA_INTERNET',
  'SOFTWARE','SEGURIDAD_SOCIAL','PERSONAL','COMISIONES_BANCARIAS','TRANSPORTE',
  'COMBUSTIBLE','MANTENIMIENTO','REPARACIONES','PUBLICIDAD','MATERIAL',
  'SERVICIOS_PROFESIONALES','IMPUESTOS','MERCANCIA','OTROS',
];
const ESTADOS_FILTER = ['Todos', 'PREVISTO', 'PENDIENTE', 'PAGADO', 'VENCIDO', 'CANCELADO'];
const DEPARTAMENTOS = ['', 'TIENDA', 'SALA_CORTE', 'CORPORATIVO', 'DISTRIBUCION'];
const PAGE_SIZES = [25, 50, 100];

const CAT_ICONS = {
  ALQUILER: 'Home', ELECTRICIDAD: 'Zap', AGUA: 'Droplets', GESTORIA: 'Briefcase',
  SEGUROS: 'ShieldCheck', TELEFONIA_INTERNET: 'Wifi', SOFTWARE: 'Monitor',
  SEGURIDAD_SOCIAL: 'Shield', PERSONAL: 'Users', COMISIONES_BANCARIAS: 'CreditCard',
  TRANSPORTE: 'Truck', COMBUSTIBLE: 'Fuel', MANTENIMIENTO: 'Wrench',
  REPARACIONES: 'Hammer', PUBLICIDAD: 'Megaphone', MATERIAL: 'Package',
  SERVICIOS_PROFESIONALES: 'UserCheck', IMPUESTOS: 'Receipt', MERCANCIA: 'ShoppingBag', OTROS: 'MoreHorizontal',
};

const ESTADO_COLORS = {
  PREVISTO: '#60A5FA', PENDIENTE: '#FBBF24', PAGADO: '#34D399',
  VENCIDO: '#F87171', CANCELADO: 'rgba(255,255,255,0.25)',
};

const CHART_COLORS = ['#F87171', '#FBBF24', '#A78BFA', '#60A5FA', '#34D399', '#D6EF4E', '#FB923C', '#E879F9'];

const fmt = (n) => (typeof n === 'number' ? n : parseFloat(n) || 0);
const fmtEur = (n) => `${fmt(n)?.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtDate = (d) => d ? new Date(d + 'T00:00:00')?.toLocaleDateString('es-ES') : '—';
const fmtDateShort = (d) => {
  if (!d) return '—';
  const dt = new Date(d + 'T00:00:00');
  return dt?.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })?.toUpperCase();
};

const inputCls = 'w-full px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/80 outline-none bg-transparent';
const inputStyle = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' };

// Smart ordering: VENCIDO > PENDIENTE > PREVISTO próximos > RECURRENTE activo > PAGADO
const smartSort = (gastos) => {
  const ORDER = { VENCIDO: 0, PENDIENTE: 1, PREVISTO: 2, PAGADO: 3, CANCELADO: 4 };
  return [...gastos]?.sort((a, b) => {
    const oa = ORDER?.[a?.estado] ?? 5;
    const ob = ORDER?.[b?.estado] ?? 5;
    if (oa !== ob) return oa - ob;
    const da = a?.fecha_vencimiento || a?.fecha || '';
    const db = b?.fecha_vencimiento || b?.fecha || '';
    return da?.localeCompare(db);
  });
};

// ─── CHECK FINANCIAL IMPACT ───────────────────────────────────────────────────
const checkGastoImpacto = async (gastoId) => {
  try {
    const { data } = await supabase
      ?.from('fin_movimientos_tesoreria')
      ?.select('id')
      ?.eq('gasto_id', gastoId)
      ?.eq('es_prevision', false)
      ?.limit(1);
    return (data || [])?.length > 0;
  } catch (_) { return false; }
};

// ─── GASTO FORM MODAL ─────────────────────────────────────────────────────────
const GastoFormModal = ({ onClose, onSave, centros, gasto }) => {
  const isEdit = !!gasto;
  const defaultRecurrencia = gasto?.recurrente_id ? 'RECURRENTE' : 'PUNTUAL';
  const [form, setForm] = useState({
    fecha: gasto?.fecha || new Date()?.toISOString()?.split('T')?.[0],
    descripcion: gasto?.descripcion || '',
    proveedor: gasto?.proveedor || '',
    proveedor_cif: gasto?.proveedor_cif || '',
    categoria: gasto?.categoria || 'OTROS',
    tipo: gasto?.tipo || 'VARIABLE',
    recurrencia: defaultRecurrencia,
    departamento: gasto?.departamento || '',
    centro_coste_id: gasto?.centro_coste_id || '',
    importe_total: gasto?.total != null ? String(gasto?.total) : '',
    iva_porcentaje: gasto?.iva_porcentaje != null ? gasto?.iva_porcentaje : 21,
    estado: gasto?.estado || 'PENDIENTE',
    fecha_vencimiento: gasto?.fecha_vencimiento || '',
    forma_pago: gasto?.forma_pago || '',
    numero_factura: gasto?.numero_factura || '',
    observaciones: gasto?.observaciones || '',
    periodicidad_pago: 'MENSUAL',
    periodicidad_imputacion: 'MENSUAL',
    dia_habitual_pago: '1',
    fecha_inicio: new Date()?.toISOString()?.split('T')?.[0],
    fecha_fin: '',
    es_estimado: false,
    activo: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const totalNum = parseFloat(form?.importe_total) || 0;
  const ivaNum = parseFloat(form?.iva_porcentaje) || 0;
  const baseCalculada = calcularBaseDesdeTotal(totalNum, ivaNum);
  const ivaCalculado = calcularIvaDesdeTotal(totalNum, ivaNum);

  const handleSave = async () => {
    if (!form?.descripcion || !form?.fecha || !form?.importe_total) {
      setError('Fecha, descripción e importe son obligatorios');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(form, baseCalculada, ivaNum, totalNum);
      onClose();
    } catch (err) {
      setError(err?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const isRecurrente = form?.recurrencia === 'RECURRENTE';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="w-full max-w-2xl rounded-2xl p-6 max-h-[90vh] overflow-y-auto" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Icon name={isEdit ? 'Edit3' : 'Plus'} size={16} className={isEdit ? 'text-[#60A5FA]' : 'text-[#D6EF4E]'} />
            <h2 className="font-oranienbaum font-bold text-white text-lg">{isEdit ? 'Editar Gasto' : 'Nuevo Gasto'}</h2>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white/80"><Icon name="X" size={18} /></button>
        </div>
        {error && <div className="mb-4 p-3 rounded-xl text-xs font-oranienbaum text-red-400" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>{error}</div>}
        {isEdit && gasto?.estado === 'PAGADO' && (
          <div className="mb-4 p-3 rounded-xl flex items-start gap-2" style={{ background: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.20)' }}>
            <Icon name="Info" size={13} className="text-[#60A5FA] flex-shrink-0 mt-0.5" />
            <p className="font-oranienbaum text-[#60A5FA]/80 text-[10px]">Gasto <strong>PAGADO</strong>. Si modificas el importe, el movimiento de tesorería se actualizará.</p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Tipo de Gasto</label>
            <div className="flex gap-2">
              {['FIJO', 'VARIABLE']?.map(t => (
                <button key={t} onClick={() => setForm(p => ({ ...p, tipo: t }))}
                  className="flex-1 py-2 rounded-xl text-xs font-oranienbaum font-semibold transition-all"
                  style={form?.tipo === t
                    ? { background: t === 'FIJO' ? 'rgba(251,191,36,0.20)' : 'rgba(167,139,250,0.20)', color: t === 'FIJO' ? '#FBBF24' : '#A78BFA', border: `1px solid ${t === 'FIJO' ? 'rgba(251,191,36,0.40)' : 'rgba(167,139,250,0.40)'}` }
                    : { background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.35)', border: '1px solid rgba(255,255,255,0.08)' }}>{t}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Recurrencia</label>
            <div className="flex gap-2">
              {['PUNTUAL', 'RECURRENTE']?.map(r => (
                <button key={r} onClick={() => !isEdit && setForm(p => ({ ...p, recurrencia: r }))}
                  disabled={isEdit}
                  className="flex-1 py-2 rounded-xl text-xs font-oranienbaum font-semibold transition-all disabled:cursor-not-allowed"
                  style={form?.recurrencia === r
                    ? { background: r === 'RECURRENTE' ? 'rgba(96,165,250,0.20)' : 'rgba(52,211,153,0.20)', color: r === 'RECURRENTE' ? '#60A5FA' : '#34D399', border: `1px solid ${r === 'RECURRENTE' ? 'rgba(96,165,250,0.40)' : 'rgba(52,211,153,0.40)'}` }
                    : { background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.35)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  {r === 'PUNTUAL' ? '● Puntual' : '↺ Recurrente'}
                </button>
              ))}
            </div>
          </div>
          <div className="md:col-span-2 p-2 rounded-xl flex items-start gap-2" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <Icon name="Info" size={11} className="text-white/25 flex-shrink-0 mt-0.5" />
            <p className="font-oranienbaum text-white/30 text-[9px]">
              <strong className="text-white/50">FIJO/VARIABLE</strong> = naturaleza del coste.&nbsp;
              <strong className="text-white/50">PUNTUAL/RECURRENTE</strong> = si se repite.&nbsp;
              Alquiler = FIJO+RECURRENTE · Electricidad = VARIABLE+RECURRENTE · Reparación = VARIABLE+PUNTUAL
            </p>
          </div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Fecha *</label>
            <input type="date" value={form?.fecha} onChange={e => setForm(p => ({ ...p, fecha: e?.target?.value }))} className={inputCls} style={inputStyle} /></div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Número Factura</label>
            <input type="text" value={form?.numero_factura} onChange={e => setForm(p => ({ ...p, numero_factura: e?.target?.value }))} className={inputCls} style={inputStyle} /></div>
          <div className="md:col-span-2"><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Descripción *</label>
            <input type="text" value={form?.descripcion} onChange={e => setForm(p => ({ ...p, descripcion: e?.target?.value }))} className={inputCls} style={inputStyle} /></div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Proveedor</label>
            <input type="text" value={form?.proveedor} onChange={e => setForm(p => ({ ...p, proveedor: e?.target?.value }))} className={inputCls} style={inputStyle} /></div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">CIF/NIF Proveedor</label>
            <input type="text" value={form?.proveedor_cif} onChange={e => setForm(p => ({ ...p, proveedor_cif: e?.target?.value }))} className={inputCls} style={inputStyle} /></div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Importe Total € (IVA incluido) *</label>
            <input type="number" step="0.01" value={form?.importe_total} onChange={e => setForm(p => ({ ...p, importe_total: e?.target?.value }))} className={inputCls} style={inputStyle} placeholder="475.00" /></div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">IVA %</label>
            <select value={form?.iva_porcentaje} onChange={e => setForm(p => ({ ...p, iva_porcentaje: Number(e?.target?.value) }))} className={inputCls} style={inputStyle}>
              {IVA_OPTIONS?.map(v => <option key={v} value={v} style={{ background: '#141414' }}>{v}%</option>)}
            </select></div>
          <div className="md:col-span-2 p-3 rounded-xl flex items-center justify-between" style={{ background: 'rgba(214,239,78,0.04)', border: '1px solid rgba(214,239,78,0.15)' }}>
            <div className="flex items-center gap-4">
              <div><p className="font-oranienbaum text-white/30 text-[9px] uppercase">Base</p><p className="font-oranienbaum text-white/70 text-xs font-semibold">{fmtEur(baseCalculada)}</p></div>
              <Icon name="Plus" size={10} className="text-white/20" />
              <div><p className="font-oranienbaum text-white/30 text-[9px] uppercase">IVA ({ivaNum}%)</p><p className="font-oranienbaum text-white/70 text-xs font-semibold">{fmtEur(ivaCalculado)}</p></div>
              <Icon name="Equal" size={10} className="text-white/20" />
              <div><p className="font-oranienbaum text-white/30 text-[9px] uppercase">Total</p><p className="font-oranienbaum text-[#D6EF4E] text-sm font-bold">{fmtEur(totalNum)}</p></div>
            </div>
          </div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Categoría</label>
            <select value={form?.categoria} onChange={e => setForm(p => ({ ...p, categoria: e?.target?.value }))} className={inputCls} style={inputStyle}>
              {CATEGORIAS?.filter(c => c !== 'Todos')?.map(o => <option key={o} value={o} style={{ background: '#141414' }}>{o}</option>)}
            </select></div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Estado</label>
            <select value={form?.estado} onChange={e => setForm(p => ({ ...p, estado: e?.target?.value }))} className={inputCls} style={inputStyle}>
              {['PENDIENTE', 'PAGADO', 'VENCIDO', 'PREVISTO']?.map(o => <option key={o} value={o} style={{ background: '#141414' }}>{o}</option>)}
            </select></div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Forma de Pago</label>
            <select value={form?.forma_pago} onChange={e => setForm(p => ({ ...p, forma_pago: e?.target?.value }))} className={inputCls} style={inputStyle}>
              {['', 'TRANSFERENCIA', 'DOMICILIACION', 'EFECTIVO', 'TARJETA', 'CHEQUE', 'OTRO']?.map(o => <option key={o} value={o} style={{ background: '#141414' }}>{o || '— Sin especificar —'}</option>)}
            </select></div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Departamento</label>
            <select value={form?.departamento} onChange={e => setForm(p => ({ ...p, departamento: e?.target?.value }))} className={inputCls} style={inputStyle}>
              {DEPARTAMENTOS?.map(d => <option key={d} value={d} style={{ background: '#141414' }}>{d || '— Sin asignar —'}</option>)}
            </select></div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Centro de Coste</label>
            <select value={form?.centro_coste_id} onChange={e => setForm(p => ({ ...p, centro_coste_id: e?.target?.value }))} className={inputCls} style={inputStyle}>
              <option value="" style={{ background: '#141414' }}>— Sin asignar —</option>
              {centros?.map(c => <option key={c?.id} value={c?.id} style={{ background: '#141414' }}>{c?.nombre}</option>)}
            </select></div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Fecha Vencimiento</label>
            <input type="date" value={form?.fecha_vencimiento} onChange={e => setForm(p => ({ ...p, fecha_vencimiento: e?.target?.value }))} className={inputCls} style={inputStyle} /></div>
          {isRecurrente && !isEdit && (
            <>
              <div className="md:col-span-2 mt-2">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-px flex-1" style={{ background: 'rgba(96,165,250,0.20)' }} />
                  <span className="font-oranienbaum text-[#60A5FA] text-[10px] uppercase tracking-wider">Configuración Recurrente</span>
                  <div className="h-px flex-1" style={{ background: 'rgba(96,165,250,0.20)' }} />
                </div>
              </div>
              <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Periodicidad de Pago</label>
                <select value={form?.periodicidad_pago} onChange={e => setForm(p => ({ ...p, periodicidad_pago: e?.target?.value }))} className={inputCls} style={inputStyle}>
                  {PERIODICIDADES?.map(p => <option key={p} value={p} style={{ background: '#141414' }}>{p}</option>)}
                </select></div>
              <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Periodicidad de Imputación</label>
                <select value={form?.periodicidad_imputacion} onChange={e => setForm(p => ({ ...p, periodicidad_imputacion: e?.target?.value }))} className={inputCls} style={inputStyle}>
                  {PERIODICIDADES?.map(p => <option key={p} value={p} style={{ background: '#141414' }}>{p}</option>)}
                </select></div>
              <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Día Habitual de Pago (1-31)</label>
                <input type="number" min="1" max="31" value={form?.dia_habitual_pago} onChange={e => setForm(p => ({ ...p, dia_habitual_pago: e?.target?.value }))} className={inputCls} style={inputStyle} placeholder="1" /></div>
              <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Fecha Inicio / Próximo Pago *</label>
                <input type="date" value={form?.fecha_inicio} onChange={e => setForm(p => ({ ...p, fecha_inicio: e?.target?.value }))} className={inputCls} style={inputStyle} /></div>
              <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Fecha Fin (opcional)</label>
                <input type="date" value={form?.fecha_fin} onChange={e => setForm(p => ({ ...p, fecha_fin: e?.target?.value }))} className={inputCls} style={inputStyle} /></div>
              <div className="flex items-center gap-3 pt-4">
                <label className="font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider">Importe Estimado</label>
                <button onClick={() => setForm(p => ({ ...p, es_estimado: !p?.es_estimado }))}
                  className="w-10 h-5 rounded-full transition-all relative"
                  style={{ background: form?.es_estimado ? '#60A5FA' : 'rgba(255,255,255,0.10)' }}>
                  <div className="w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all" style={{ left: form?.es_estimado ? '22px' : '2px' }} />
                </button>
              </div>
            </>
          )}
          <div className="md:col-span-2"><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Observaciones</label>
            <input type="text" value={form?.observaciones} onChange={e => setForm(p => ({ ...p, observaciones: e?.target?.value }))} className={inputCls} style={inputStyle} /></div>
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-xs font-oranienbaum text-white/50 hover:text-white/80" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-xl text-xs font-oranienbaum font-semibold text-black disabled:opacity-50" style={{ background: isEdit ? '#60A5FA' : '#D6EF4E' }}>
            {saving ? 'Guardando...' : isEdit ? 'Guardar Cambios' : isRecurrente ? 'Crear + Generar Previsiones' : 'Guardar Gasto'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── RECURRENTE FORM MODAL ────────────────────────────────────────────────────
const RecurrenteFormModal = ({ onClose, onSave, centros, recurrente }) => {
  const isEdit = !!recurrente;
  const [form, setForm] = useState({
    nombre: recurrente?.nombre || '',
    proveedor: recurrente?.proveedor || '',
    proveedor_cif: recurrente?.proveedor_cif || '',
    categoria: recurrente?.categoria || 'OTROS',
    tipo: recurrente?.tipo || 'FIJO',
    departamento: recurrente?.departamento || '',
    centro_coste_id: recurrente?.centro_coste_id || '',
    importe_total: recurrente?.importe_total != null ? String(recurrente?.importe_total) : (recurrente?.importe_habitual != null ? String(Math.round(recurrente?.importe_habitual * 1.21 * 100) / 100) : ''),
    iva_porcentaje: recurrente?.iva_porcentaje != null ? recurrente?.iva_porcentaje : 21,
    periodicidad_pago: recurrente?.periodicidad_pago || recurrente?.periodicidad || 'MENSUAL',
    periodicidad_imputacion: recurrente?.periodicidad_imputacion || recurrente?.periodicidad || 'MENSUAL',
    dia_habitual_pago: recurrente?.dia_habitual_pago || recurrente?.dia_pago || '',
    fecha_inicio: recurrente?.fecha_inicio || recurrente?.proximo_vencimiento || new Date()?.toISOString()?.split('T')?.[0],
    fecha_fin: recurrente?.fecha_fin || '',
    forma_pago: recurrente?.forma_pago || '',
    activo: recurrente?.activo !== false,
    observaciones: recurrente?.observaciones || '',
    es_estimado: recurrente?.es_estimado || false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const totalNum = parseFloat(form?.importe_total) || 0;
  const ivaNum = parseFloat(form?.iva_porcentaje) || 0;
  const baseCalculada = calcularBaseDesdeTotal(totalNum, ivaNum);
  const ivaCalculado = calcularIvaDesdeTotal(totalNum, ivaNum);
  const divisores = { MENSUAL: 1, BIMESTRAL: 2, TRIMESTRAL: 3, SEMESTRAL: 6, ANUAL: 12 };
  const costeMensualNorm = Math.round((baseCalculada / (divisores?.[form?.periodicidad_imputacion] || 1)) * 100) / 100;

  const handleSave = async () => {
    if (!form?.nombre || !form?.importe_total) { setError('Nombre e importe son obligatorios'); return; }
    setSaving(true); setError(null);
    try {
      const payload = {
        nombre: form?.nombre, proveedor: form?.proveedor || null, proveedor_cif: form?.proveedor_cif || null,
        categoria: form?.categoria, tipo: form?.tipo, departamento: form?.departamento || null,
        centro_coste_id: form?.centro_coste_id || null, importe_total: totalNum, importe_habitual: baseCalculada,
        iva_porcentaje: ivaNum, periodicidad_pago: form?.periodicidad_pago, periodicidad_imputacion: form?.periodicidad_imputacion,
        periodicidad: form?.periodicidad_pago,
        dia_habitual_pago: form?.dia_habitual_pago ? parseInt(form?.dia_habitual_pago) : 1,
        dia_pago: form?.dia_habitual_pago ? parseInt(form?.dia_habitual_pago) : null,
        fecha_inicio: form?.fecha_inicio || null, proximo_vencimiento: form?.fecha_inicio || null,
        fecha_fin: form?.fecha_fin || null, forma_pago: form?.forma_pago || null,
        activo: form?.activo, observaciones: form?.observaciones || null, es_estimado: form?.es_estimado,
      };
      await onSave(payload);
      onClose();
    } catch (err) { setError(err?.message || 'Error al guardar'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="w-full max-w-2xl rounded-2xl p-6 max-h-[90vh] overflow-y-auto" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Icon name={isEdit ? 'Edit3' : 'Repeat'} size={16} className="text-[#FBBF24]" />
            <h2 className="font-oranienbaum font-bold text-white text-lg">{isEdit ? 'Editar Recurrente' : 'Nuevo Gasto Recurrente'}</h2>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white/80"><Icon name="X" size={18} /></button>
        </div>
        {error && <div className="mb-4 p-3 rounded-xl text-xs font-oranienbaum text-red-400" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>{error}</div>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2"><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Nombre del Gasto *</label>
            <input type="text" value={form?.nombre} onChange={e => setForm(p => ({ ...p, nombre: e?.target?.value }))} className={inputCls} style={inputStyle} placeholder="Ej: Alquiler local" /></div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Proveedor</label>
            <input type="text" value={form?.proveedor} onChange={e => setForm(p => ({ ...p, proveedor: e?.target?.value }))} className={inputCls} style={inputStyle} /></div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">CIF Proveedor</label>
            <input type="text" value={form?.proveedor_cif} onChange={e => setForm(p => ({ ...p, proveedor_cif: e?.target?.value }))} className={inputCls} style={inputStyle} /></div>
          <div>
            <label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Tipo de Gasto</label>
            <div className="flex gap-2">
              {['FIJO', 'VARIABLE']?.map(t => (
                <button key={t} onClick={() => setForm(p => ({ ...p, tipo: t }))}
                  className="flex-1 py-2 rounded-xl text-xs font-oranienbaum font-semibold transition-all"
                  style={form?.tipo === t
                    ? { background: t === 'FIJO' ? 'rgba(251,191,36,0.20)' : 'rgba(167,139,250,0.20)', color: t === 'FIJO' ? '#FBBF24' : '#A78BFA', border: `1px solid ${t === 'FIJO' ? 'rgba(251,191,36,0.40)' : 'rgba(167,139,250,0.40)'}` }
                    : { background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.35)', border: '1px solid rgba(255,255,255,0.08)' }}>{t}</button>
              ))}
            </div>
          </div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Importe Total € (IVA incluido) *</label>
            <input type="number" step="0.01" value={form?.importe_total} onChange={e => setForm(p => ({ ...p, importe_total: e?.target?.value }))} className={inputCls} style={inputStyle} placeholder="475.00" /></div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">IVA %</label>
            <select value={form?.iva_porcentaje} onChange={e => setForm(p => ({ ...p, iva_porcentaje: Number(e?.target?.value) }))} className={inputCls} style={inputStyle}>
              {IVA_OPTIONS?.map(v => <option key={v} value={v} style={{ background: '#141414' }}>{v}%</option>)}
            </select></div>
          <div className="md:col-span-2 p-3 rounded-xl" style={{ background: 'rgba(251,191,36,0.04)', border: '1px solid rgba(251,191,36,0.15)' }}>
            <div className="flex items-center gap-4 flex-wrap">
              <div><p className="font-oranienbaum text-white/30 text-[9px] uppercase">Base</p><p className="font-oranienbaum text-white/70 text-xs font-semibold">{fmtEur(baseCalculada)}</p></div>
              <Icon name="Plus" size={10} className="text-white/20" />
              <div><p className="font-oranienbaum text-white/30 text-[9px] uppercase">IVA ({ivaNum}%)</p><p className="font-oranienbaum text-white/70 text-xs font-semibold">{fmtEur(ivaCalculado)}</p></div>
              <Icon name="Equal" size={10} className="text-white/20" />
              <div><p className="font-oranienbaum text-white/30 text-[9px] uppercase">Total</p><p className="font-oranienbaum text-[#FBBF24] text-sm font-bold">{fmtEur(totalNum)}</p></div>
              <div className="ml-auto text-right">
                <p className="font-oranienbaum text-white/30 text-[9px] uppercase">Coste mensual norm.</p>
                <p className="font-oranienbaum text-[#34D399] text-xs font-semibold">{fmtEur(costeMensualNorm)}/mes</p>
              </div>
            </div>
          </div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Periodicidad Pago (Tesorería)</label>
            <select value={form?.periodicidad_pago} onChange={e => setForm(p => ({ ...p, periodicidad_pago: e?.target?.value }))} className={inputCls} style={inputStyle}>
              {PERIODICIDADES?.map(p => <option key={p} value={p} style={{ background: '#141414' }}>{p}</option>)}
            </select></div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Periodicidad Imputación (Gestión)</label>
            <select value={form?.periodicidad_imputacion} onChange={e => setForm(p => ({ ...p, periodicidad_imputacion: e?.target?.value }))} className={inputCls} style={inputStyle}>
              {PERIODICIDADES?.map(p => <option key={p} value={p} style={{ background: '#141414' }}>{p}</option>)}
            </select></div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Día Habitual de Pago (1-31)</label>
            <input type="number" min="1" max="31" value={form?.dia_habitual_pago} onChange={e => setForm(p => ({ ...p, dia_habitual_pago: e?.target?.value }))} className={inputCls} style={inputStyle} placeholder="1" /></div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Fecha Inicio / Próximo Pago</label>
            <input type="date" value={form?.fecha_inicio} onChange={e => setForm(p => ({ ...p, fecha_inicio: e?.target?.value }))} className={inputCls} style={inputStyle} /></div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Fecha Fin (opcional)</label>
            <input type="date" value={form?.fecha_fin} onChange={e => setForm(p => ({ ...p, fecha_fin: e?.target?.value }))} className={inputCls} style={inputStyle} /></div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Categoría</label>
            <select value={form?.categoria} onChange={e => setForm(p => ({ ...p, categoria: e?.target?.value }))} className={inputCls} style={inputStyle}>
              {CATEGORIAS?.filter(c => c !== 'Todos')?.map(o => <option key={o} value={o} style={{ background: '#141414' }}>{o}</option>)}
            </select></div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Departamento</label>
            <select value={form?.departamento} onChange={e => setForm(p => ({ ...p, departamento: e?.target?.value }))} className={inputCls} style={inputStyle}>
              {DEPARTAMENTOS?.map(d => <option key={d} value={d} style={{ background: '#141414' }}>{d || '— Sin asignar —'}</option>)}
            </select></div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Centro de Coste</label>
            <select value={form?.centro_coste_id} onChange={e => setForm(p => ({ ...p, centro_coste_id: e?.target?.value }))} className={inputCls} style={inputStyle}>
              <option value="" style={{ background: '#141414' }}>— Sin asignar —</option>
              {centros?.map(c => <option key={c?.id} value={c?.id} style={{ background: '#141414' }}>{c?.nombre}</option>)}
            </select></div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Forma de Pago</label>
            <select value={form?.forma_pago} onChange={e => setForm(p => ({ ...p, forma_pago: e?.target?.value }))} className={inputCls} style={inputStyle}>
              {['', 'TRANSFERENCIA', 'DOMICILIACION', 'EFECTIVO', 'TARJETA', 'CHEQUE', 'OTRO']?.map(o => <option key={o} value={o} style={{ background: '#141414' }}>{o || '— Sin especificar —'}</option>)}
            </select></div>
          <div className="flex items-center gap-3 pt-4">
            <label className="font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider">Activo</label>
            <button onClick={() => setForm(p => ({ ...p, activo: !p?.activo }))} className="w-10 h-5 rounded-full transition-all relative" style={{ background: form?.activo ? '#34D399' : 'rgba(255,255,255,0.10)' }}>
              <div className="w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all" style={{ left: form?.activo ? '22px' : '2px' }} />
            </button>
          </div>
          <div className="flex items-center gap-3 pt-4">
            <label className="font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider">Es Estimado</label>
            <button onClick={() => setForm(p => ({ ...p, es_estimado: !p?.es_estimado }))} className="w-10 h-5 rounded-full transition-all relative" style={{ background: form?.es_estimado ? '#60A5FA' : 'rgba(255,255,255,0.10)' }}>
              <div className="w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all" style={{ left: form?.es_estimado ? '22px' : '2px' }} />
            </button>
          </div>
          <div className="md:col-span-2"><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Observaciones</label>
            <input type="text" value={form?.observaciones} onChange={e => setForm(p => ({ ...p, observaciones: e?.target?.value }))} className={inputCls} style={inputStyle} /></div>
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-xs font-oranienbaum text-white/50 hover:text-white/80" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-xl text-xs font-oranienbaum font-semibold text-black disabled:opacity-50" style={{ background: '#FBBF24' }}>
            {saving ? 'Guardando...' : isEdit ? 'Guardar Cambios' : 'Crear Recurrente'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── ACTUALIZAR IMPORTE FUTURO MODAL ─────────────────────────────────────────
const ActualizarImporteModal = ({ recurrente, onClose, onConfirm }) => {
  const [nuevoTotal, setNuevoTotal] = useState(String(recurrente?.importe_total || ''));
  const [ivaPct, setIvaPct] = useState(recurrente?.iva_porcentaje || 21);
  const [desdeFecha, setDesdeFecha] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [saving, setSaving] = useState(false);
  const base = calcularBaseDesdeTotal(parseFloat(nuevoTotal) || 0, ivaPct);
  const iva = calcularIvaDesdeTotal(parseFloat(nuevoTotal) || 0, ivaPct);

  const handleConfirm = async () => {
    setSaving(true);
    try { await onConfirm(parseFloat(nuevoTotal), ivaPct, desdeFecha); onClose(); }
    catch (err) { alert(err?.message); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="w-full max-w-md rounded-2xl p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-oranienbaum font-bold text-white text-base">Actualizar Importe Futuro</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white/80"><Icon name="X" size={16} /></button>
        </div>
        <p className="font-oranienbaum text-white/50 text-xs mb-4">Los pagos históricos ya realizados <strong className="text-white/70">no se modificarán</strong>. Solo se actualizarán las previsiones futuras.</p>
        <div className="space-y-3">
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Nuevo Importe Total (IVA incluido)</label>
            <input type="number" step="0.01" value={nuevoTotal} onChange={e => setNuevoTotal(e?.target?.value)} className={inputCls} style={inputStyle} /></div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">IVA %</label>
            <select value={ivaPct} onChange={e => setIvaPct(Number(e?.target?.value))} className={inputCls} style={inputStyle}>
              {IVA_OPTIONS?.map(v => <option key={v} value={v} style={{ background: '#141414' }}>{v}%</option>)}
            </select></div>
          <div className="p-3 rounded-xl flex gap-4" style={{ background: 'rgba(251,191,36,0.04)', border: '1px solid rgba(251,191,36,0.15)' }}>
            <div><p className="font-oranienbaum text-white/30 text-[9px]">Base</p><p className="font-oranienbaum text-white/70 text-xs">{fmtEur(base)}</p></div>
            <div><p className="font-oranienbaum text-white/30 text-[9px]">IVA</p><p className="font-oranienbaum text-white/70 text-xs">{fmtEur(iva)}</p></div>
            <div><p className="font-oranienbaum text-white/30 text-[9px]">Total</p><p className="font-oranienbaum text-[#FBBF24] text-xs font-bold">{fmtEur(parseFloat(nuevoTotal) || 0)}</p></div>
          </div>
          <div><label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Aplicar desde</label>
            <input type="date" value={desdeFecha} onChange={e => setDesdeFecha(e?.target?.value)} className={inputCls} style={inputStyle} /></div>
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-xs font-oranienbaum text-white/50" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>Cancelar</button>
          <button onClick={handleConfirm} disabled={saving} className="px-4 py-2 rounded-xl text-xs font-oranienbaum font-semibold text-black disabled:opacity-50" style={{ background: '#FBBF24' }}>
            {saving ? 'Actualizando...' : 'Actualizar Previsiones'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── DELETE / ANULAR CONFIRM MODAL ───────────────────────────────────────────
const DeleteConfirmModal = ({ gasto, tieneImpacto, onClose, onEliminar, onAnular }) => {
  const [loading, setLoading] = useState(false);
  const handleEliminar = async () => { setLoading(true); try { await onEliminar(); onClose(); } catch (err) { alert(err?.message); } finally { setLoading(false); } };
  const handleAnular = async () => { setLoading(true); try { await onAnular(); onClose(); } catch (err) { alert(err?.message); } finally { setLoading(false); } };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="w-full max-w-md rounded-2xl p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.25)' }}>
            <Icon name="AlertTriangle" size={18} className="text-[#F87171]" />
          </div>
          <div>
            <h3 className="font-oranienbaum font-bold text-white text-base">{tieneImpacto ? 'Anular Gasto' : 'Eliminar Gasto'}</h3>
            <p className="font-oranienbaum text-white/40 text-xs truncate max-w-[260px]">{gasto?.descripcion}</p>
          </div>
        </div>
        {tieneImpacto ? (
          <div className="mb-5 p-3 rounded-xl" style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.20)' }}>
            <p className="font-oranienbaum text-[#FBBF24]/80 text-xs">Este gasto tiene <strong>movimientos de tesorería asociados</strong>. Se marcará como <strong>CANCELADO</strong> para mantener el histórico financiero.</p>
          </div>
        ) : (
          <div className="mb-5 p-3 rounded-xl" style={{ background: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.20)' }}>
            <p className="font-oranienbaum text-[#F87171]/80 text-xs">Sin impacto financiero registrado. Se eliminará permanentemente. <strong className="block mt-1">Esta acción no se puede deshacer.</strong></p>
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button onClick={onClose} disabled={loading} className="px-4 py-2 rounded-xl text-xs font-oranienbaum text-white/50" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>Cancelar</button>
          {tieneImpacto ? (
            <button onClick={handleAnular} disabled={loading} className="px-4 py-2 rounded-xl text-xs font-oranienbaum font-semibold disabled:opacity-50" style={{ background: 'rgba(251,191,36,0.15)', color: '#FBBF24', border: '1px solid rgba(251,191,36,0.30)' }}>
              {loading ? 'Anulando...' : 'Anular (CANCELADO)'}
            </button>
          ) : (
            <button onClick={handleEliminar} disabled={loading} className="px-4 py-2 rounded-xl text-xs font-oranienbaum font-semibold text-white disabled:opacity-50" style={{ background: 'rgba(248,113,113,0.20)', border: '1px solid rgba(248,113,113,0.35)' }}>
              {loading ? 'Eliminando...' : 'Eliminar definitivamente'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── DELETE RECURRENTE MODAL ──────────────────────────────────────────────────
const DeleteRecurrenteModal = ({ recurrente, tieneHistorico, onClose, onDesactivar, onEliminar }) => {
  const [loading, setLoading] = useState(false);
  const handleDesactivar = async () => { setLoading(true); try { await onDesactivar(); onClose(); } catch (err) { alert(err?.message); } finally { setLoading(false); } };
  const handleEliminar = async () => { setLoading(true); try { await onEliminar(); onClose(); } catch (err) { alert(err?.message); } finally { setLoading(false); } };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="w-full max-w-md rounded-2xl p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.25)' }}>
            <Icon name="Trash2" size={18} className="text-[#F87171]" />
          </div>
          <div>
            <h3 className="font-oranienbaum font-bold text-white text-base">Gestionar Recurrente</h3>
            <p className="font-oranienbaum text-white/40 text-xs">{recurrente?.nombre}</p>
          </div>
        </div>
        {tieneHistorico ? (
          <div className="mb-5 p-3 rounded-xl" style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.20)' }}>
            <p className="font-oranienbaum text-[#FBBF24]/80 text-xs">Tiene ocurrencias históricas. Se recomienda <strong>desactivar</strong>: cancela previsiones futuras pero mantiene pagos históricos.</p>
          </div>
        ) : (
          <div className="mb-5 p-3 rounded-xl" style={{ background: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.20)' }}>
            <p className="font-oranienbaum text-[#F87171]/80 text-xs">Sin ocurrencias históricas. Se puede eliminar completamente.</p>
          </div>
        )}
        <div className="flex justify-end gap-3 flex-wrap">
          <button onClick={onClose} disabled={loading} className="px-4 py-2 rounded-xl text-xs font-oranienbaum text-white/50" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>Cancelar</button>
          {tieneHistorico ? (
            <button onClick={handleDesactivar} disabled={loading} className="px-4 py-2 rounded-xl text-xs font-oranienbaum font-semibold disabled:opacity-50" style={{ background: 'rgba(251,191,36,0.15)', color: '#FBBF24', border: '1px solid rgba(251,191,36,0.30)' }}>
              {loading ? 'Desactivando...' : 'Desactivar + Cancelar Previsiones'}
            </button>
          ) : (
            <button onClick={handleEliminar} disabled={loading} className="px-4 py-2 rounded-xl text-xs font-oranienbaum font-semibold text-white disabled:opacity-50" style={{ background: 'rgba(248,113,113,0.20)', border: '1px solid rgba(248,113,113,0.35)' }}>
              {loading ? 'Eliminando...' : 'Eliminar definitivamente'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── ROW ACTIONS MENU (⋯) ─────────────────────────────────────────────────────
const RowActionsMenu = ({ g, onEdit, onMarcarPagado, onRefresh, onVerCalendario, onActualizarImporte }) => {
  const [open, setOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [checking, setChecking] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (menuRef?.current && !menuRef?.current?.contains(e?.target)) setOpen(false); };
    document?.addEventListener('mousedown', handler);
    return () => document?.removeEventListener('mousedown', handler);
  }, []);

  const handleDeleteClick = async () => {
    setOpen(false);
    setChecking(true);
    try {
      const tieneImpacto = await checkGastoImpacto(g?.id);
      setDeleteModal({ gasto: g, tieneImpacto });
    } catch (_) { setDeleteModal({ gasto: g, tieneImpacto: false }); }
    finally { setChecking(false); }
  };

  const handleEliminar = async () => { await gastosService?.delete(deleteModal?.gasto?.id); onRefresh?.(); };
  const handleAnular = async () => { await gastosService?.update(deleteModal?.gasto?.id, { estado: 'CANCELADO' }); onRefresh?.(); };

  const isPagado = g?.estado === 'PAGADO';
  const isCancelado = g?.estado === 'CANCELADO';
  const isPrevision = g?.es_prevision;
  const isVencidoOrPendiente = g?.estado === 'VENCIDO' || g?.estado === 'PENDIENTE';

  return (
    <div className="relative" ref={menuRef}>
      {deleteModal && (
        <DeleteConfirmModal
          gasto={deleteModal?.gasto}
          tieneImpacto={deleteModal?.tieneImpacto}
          onClose={() => setDeleteModal(null)}
          onEliminar={handleEliminar}
          onAnular={handleAnular}
        />
      )}
      <div className="flex items-center gap-1">
        {!isCancelado && !isPrevision && (
          <button onClick={() => { setOpen(false); onEdit?.(g); }}
            className="px-2 py-1 rounded-lg text-[9px] font-oranienbaum"
            style={{ background: 'rgba(96,165,250,0.15)', color: '#60A5FA', border: '1px solid rgba(96,165,250,0.25)' }}>
            Editar
          </button>
        )}
        {isVencidoOrPendiente && !isPrevision && (
          <button onClick={() => onMarcarPagado?.(g?.id)}
            className="px-2 py-1 rounded-lg text-[9px] font-oranienbaum font-semibold text-black"
            style={{ background: '#34D399' }}>
            ✓ Pagado
          </button>
        )}
        <div className="relative">
          <button onClick={() => setOpen(v => !v)} disabled={checking}
            className="w-6 h-6 rounded-lg flex items-center justify-center text-white/40 hover:text-white/80 transition-colors disabled:opacity-50"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}>
            {checking ? <div className="w-3 h-3 rounded-full border border-white/30 border-t-white/80 animate-spin" /> : <Icon name="MoreHorizontal" size={12} />}
          </button>
          {open && (
            <div className="absolute right-0 top-8 z-50 w-44 rounded-xl overflow-hidden shadow-2xl" style={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.12)' }}>
              {!isCancelado && !isPrevision && (
                <button onClick={() => { setOpen(false); onEdit?.(g); }} className="w-full px-3 py-2 text-left text-xs font-oranienbaum text-white/70 hover:bg-white/5 flex items-center gap-2">
                  <Icon name="Edit3" size={11} className="text-[#60A5FA]" />Editar
                </button>
              )}
              {isVencidoOrPendiente && !isPrevision && (
                <button onClick={() => { setOpen(false); onMarcarPagado?.(g?.id); }} className="w-full px-3 py-2 text-left text-xs font-oranienbaum text-white/70 hover:bg-white/5 flex items-center gap-2">
                  <Icon name="CheckCircle" size={11} className="text-[#34D399]" />Marcar pagado
                </button>
              )}
              {g?.recurrente_id && onVerCalendario && (
                <button onClick={() => { setOpen(false); onVerCalendario?.(g); }} className="w-full px-3 py-2 text-left text-xs font-oranienbaum text-white/70 hover:bg-white/5 flex items-center gap-2">
                  <Icon name="Calendar" size={11} className="text-[#A78BFA]" />Ver calendario
                </button>
              )}
              {g?.recurrente_id && onActualizarImporte && (
                <button onClick={() => { setOpen(false); onActualizarImporte?.(g); }} className="w-full px-3 py-2 text-left text-xs font-oranienbaum text-white/70 hover:bg-white/5 flex items-center gap-2">
                  <Icon name="TrendingUp" size={11} className="text-[#FBBF24]" />Cambio futuro
                </button>
              )}
              {!isCancelado && (
                <button onClick={() => { setOpen(false); handleDeleteClick(); }} className="w-full px-3 py-2 text-left text-xs font-oranienbaum hover:bg-white/5 flex items-center gap-2" style={{ color: isPagado ? '#FBBF24' : '#F87171' }}>
                  <Icon name={isPagado ? 'XCircle' : 'Trash2'} size={11} style={{ color: isPagado ? '#FBBF24' : '#F87171' }} />
                  {isPagado ? 'Anular' : 'Eliminar'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── EXPANDABLE ROW DETAIL ────────────────────────────────────────────────────
const RowDetail = ({ g }) => {
  const [calendarData, setCalendarData] = useState([]);
  const [loadingCal, setLoadingCal] = useState(false);
  const [historico, setHistorico] = useState([]);

  useEffect(() => {
    if (!g?.recurrente_id) return;
    setLoadingCal(true);
    Promise.all([
      supabase?.from('fin_gastos')?.select('fecha_vencimiento, estado, total, base_imponible, iva_porcentaje')
        ?.eq('recurrente_id', g?.recurrente_id)?.gte('fecha_vencimiento', new Date()?.toISOString()?.split('T')?.[0])
        ?.order('fecha_vencimiento', { ascending: true })?.limit(6),
      supabase?.from('fin_gastos')?.select('fecha_vencimiento, estado, total')
        ?.eq('recurrente_id', g?.recurrente_id)?.lt('fecha_vencimiento', new Date()?.toISOString()?.split('T')?.[0])
        ?.order('fecha_vencimiento', { ascending: false })?.limit(4),
    ])?.then(([futRes, histRes]) => {
      setCalendarData(futRes?.data || []);
      setHistorico(histRes?.data || []);
    })?.finally(() => setLoadingCal(false));
  }, [g?.recurrente_id]);

  const total = g?.total || (g?.base_imponible * (1 + (g?.iva_porcentaje || 21) / 100));

  return (
    <div className="px-4 py-4 grid grid-cols-1 md:grid-cols-3 gap-4" style={{ background: 'rgba(255,255,255,0.015)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
      {/* Desglose financiero */}
      <div className="space-y-2">
        <p className="font-oranienbaum text-white/30 text-[10px] uppercase tracking-wider mb-2">Desglose Financiero</p>
        <div className="flex justify-between"><span className="font-oranienbaum text-white/40 text-xs">Base imponible</span><span className="font-oranienbaum text-white/70 text-xs">{fmtEur(g?.base_imponible)}</span></div>
        <div className="flex justify-between"><span className="font-oranienbaum text-white/40 text-xs">IVA ({g?.iva_porcentaje || 21}%)</span><span className="font-oranienbaum text-white/70 text-xs">{fmtEur(g?.iva_importe || (g?.base_imponible * (g?.iva_porcentaje || 21) / 100))}</span></div>
        <div className="flex justify-between border-t border-white/8 pt-1"><span className="font-oranienbaum text-white/60 text-xs font-semibold">Total</span><span className="font-oranienbaum text-[#D6EF4E] text-xs font-bold">{fmtEur(total)}</span></div>
        {g?.forma_pago && <div className="flex justify-between"><span className="font-oranienbaum text-white/40 text-xs">Forma pago</span><span className="font-oranienbaum text-white/60 text-xs">{g?.forma_pago}</span></div>}
        {g?.numero_factura && <div className="flex justify-between"><span className="font-oranienbaum text-white/40 text-xs">Nº Factura</span><span className="font-oranienbaum text-white/60 text-xs">{g?.numero_factura}</span></div>}
        {g?.observaciones && <div className="mt-2 p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}><p className="font-oranienbaum text-white/40 text-[10px]">{g?.observaciones}</p></div>}
      </div>
      {/* Histórico */}
      {g?.recurrente_id && (
        <div>
          <p className="font-oranienbaum text-white/30 text-[10px] uppercase tracking-wider mb-2">Histórico Reciente</p>
          {historico?.length === 0 ? <p className="font-oranienbaum text-white/20 text-xs">Sin histórico</p> : historico?.map((h, i) => (
            <div key={i} className="flex items-center justify-between py-1 border-b border-white/5">
              <span className="font-oranienbaum text-white/40 text-xs">{fmtDate(h?.fecha_vencimiento)}</span>
              <div className="flex items-center gap-2">
                <span className="font-oranienbaum text-white/60 text-xs">{fmtEur(h?.total)}</span>
                <span className="px-1.5 py-0.5 rounded text-[8px] font-oranienbaum" style={{ background: `${ESTADO_COLORS?.[h?.estado] || '#60A5FA'}15`, color: ESTADO_COLORS?.[h?.estado] || '#60A5FA' }}>{h?.estado}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Próximos vencimientos */}
      {g?.recurrente_id && (
        <div>
          <p className="font-oranienbaum text-white/30 text-[10px] uppercase tracking-wider mb-2">Próximos Vencimientos</p>
          {loadingCal ? <div className="w-4 h-4 rounded-full border border-white/20 border-t-[#60A5FA] animate-spin" /> :
            calendarData?.length === 0 ? <p className="font-oranienbaum text-white/20 text-xs">Sin previsiones futuras</p> :
            calendarData?.map((c, i) => (
              <div key={i} className="flex items-center justify-between py-1 border-b border-white/5">
                <span className="font-oranienbaum text-white/40 text-xs">{fmtDate(c?.fecha_vencimiento)}</span>
                <div className="flex items-center gap-2">
                  <span className="font-oranienbaum text-white/60 text-xs">{fmtEur(c?.total || (c?.base_imponible * (1 + (c?.iva_porcentaje || 21) / 100)))}</span>
                  <span className="px-1.5 py-0.5 rounded text-[8px] font-oranienbaum" style={{ background: `${ESTADO_COLORS?.[c?.estado] || '#60A5FA'}15`, color: ESTADO_COLORS?.[c?.estado] || '#60A5FA' }}>{c?.estado}</span>
                </div>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
};

// ─── CALENDARIO MODAL ─────────────────────────────────────────────────────────
const CalendarioModal = ({ onClose, onMarcarPagado }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [isMobile, setIsMobile] = useState(window?.innerWidth < 640);

  useEffect(() => {
    const handler = () => setIsMobile(window?.innerWidth < 640);
    window?.addEventListener('resize', handler);
    return () => window?.removeEventListener('resize', handler);
  }, []);

  const loadPagos = useCallback(async () => {
    setLoading(true);
    try {
      const year = currentDate?.getFullYear();
      const month = currentDate?.getMonth();
      const pad = (n) => String(n)?.padStart(2, '0');
      const from = `${year}-${pad(month + 1)}-01`;
      const lastDay = new Date(year, month + 1, 0)?.getDate();
      const to = `${year}-${pad(month + 1)}-${pad(lastDay)}`;
      const { data } = await supabase
        ?.from('fin_gastos')
        ?.select('id, fecha, fecha_vencimiento, descripcion, proveedor, total, base_imponible, iva_porcentaje, estado, es_prevision, recurrente_id')
        ?.neq('estado', 'CANCELADO')
        ?.order('fecha_vencimiento', { ascending: true });
      const filtered = (data || [])?.filter(p => {
        const d = p?.fecha_vencimiento || p?.fecha;
        return d >= from && d <= to;
      });
      setPagos(filtered);
    } catch (_) {} finally { setLoading(false); }
  }, [currentDate]);

  useEffect(() => { loadPagos(); }, [loadPagos]);

  const year = currentDate?.getFullYear();
  const month = currentDate?.getMonth();
  const firstDay = new Date(year, month, 1)?.getDay();
  const daysInMonth = new Date(year, month + 1, 0)?.getDate();
  const startOffset = firstDay === 0 ? 6 : firstDay - 1;

  const pagosByDay = useMemo(() => {
    const map = {};
    pagos?.forEach(p => {
      const d = p?.fecha_vencimiento || p?.fecha;
      if (!d) return;
      const day = parseInt(d?.split('-')?.[2]);
      if (!map?.[day]) map[day] = [];
      map?.[day]?.push(p);
    });
    return map;
  }, [pagos]);

  const totalMes = pagos?.reduce((s, p) => s + fmt(p?.total || (p?.base_imponible * (1 + (p?.iva_porcentaje || 21) / 100))), 0);
  const totalPagado = pagos?.filter(p => p?.estado === 'PAGADO')?.reduce((s, p) => s + fmt(p?.total || 0), 0);
  const totalPendiente = pagos?.filter(p => p?.estado === 'PENDIENTE' || p?.estado === 'VENCIDO')?.reduce((s, p) => s + fmt(p?.total || 0), 0);
  const totalPrevisto = pagos?.filter(p => p?.estado === 'PREVISTO')?.reduce((s, p) => s + fmt(p?.total || 0), 0);
  const hoy = new Date();
  const monthLabel = currentDate?.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })?.toUpperCase();
  const DAYS_HEADER = ['LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB', 'DOM'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-5xl rounded-2xl flex flex-col max-h-[95vh]" style={{ background: '#0F0F0F', border: '1px solid rgba(255,255,255,0.10)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
          <div className="flex items-center gap-3">
            <Icon name="Calendar" size={16} className="text-[#60A5FA]" />
            <h2 className="font-oranienbaum font-bold text-white text-base">Calendario de Pagos</h2>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white/80"><Icon name="X" size={18} /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {/* Nav + KPIs */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentDate(new Date(year, month - 1, 1))} className="w-8 h-8 rounded-lg flex items-center justify-center text-white/50 hover:text-white" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}><Icon name="ChevronLeft" size={14} /></button>
              <span className="font-oranienbaum font-bold text-white text-sm min-w-[180px] text-center">{monthLabel}</span>
              <button onClick={() => setCurrentDate(new Date(year, month + 1, 1))} className="w-8 h-8 rounded-lg flex items-center justify-center text-white/50 hover:text-white" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}><Icon name="ChevronRight" size={14} /></button>
              <button onClick={() => setCurrentDate(new Date())} className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum" style={{ background: 'rgba(214,239,78,0.08)', border: '1px solid rgba(214,239,78,0.20)', color: '#D6EF4E' }}>HOY</button>
            </div>
            <div className="flex gap-2 flex-wrap">
              {[{ label: 'Total Mes', val: totalMes, color: '#60A5FA' }, { label: 'Pagado', val: totalPagado, color: '#34D399' }, { label: 'Pendiente', val: totalPendiente, color: '#FBBF24' }, { label: 'Previsto', val: totalPrevisto, color: '#A78BFA' }]?.map(k => (
                <div key={k?.label} className="px-3 py-1.5 rounded-xl text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <p className="font-oranienbaum text-[9px] mb-0.5" style={{ color: k?.color }}>{k?.label}</p>
                  <p className="font-oranienbaum text-white/80 text-xs font-semibold">{fmtEur(k?.val)}</p>
                </div>
              ))}
            </div>
          </div>
          {/* Legend */}
          <div className="flex gap-3 flex-wrap">
            {Object.entries(ESTADO_COLORS)?.filter(([k]) => k !== 'CANCELADO')?.map(([estado, color]) => (
              <div key={estado} className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ background: color }} />
                <span className="font-oranienbaum text-white/40 text-[10px]">{estado}</span>
              </div>
            ))}
          </div>
          {/* Calendar grid */}
          {loading ? (
            <div className="flex items-center justify-center py-16"><div className="w-6 h-6 rounded-full border-2 border-white/10 border-t-[#60A5FA] animate-spin" /></div>
          ) : isMobile ? (
            <div className="space-y-2">
              {Array.from({ length: daysInMonth }, (_, i) => i + 1)?.filter(day => pagosByDay?.[day]?.length > 0)?.map(day => (
                <div key={day} className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <p className="font-oranienbaum text-white/50 text-xs font-semibold mb-2">Día {day}</p>
                  {pagosByDay?.[day]?.map((p, i) => (
                    <div key={i} className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ background: ESTADO_COLORS?.[p?.estado] || '#60A5FA' }} />
                        <span className="font-oranienbaum text-white/70 text-xs">{p?.descripcion}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-oranienbaum text-white/60 text-xs">{fmtEur(p?.total || (p?.base_imponible * (1 + (p?.iva_porcentaje || 21) / 100)))}</span>
                        {(p?.estado === 'PENDIENTE' || p?.estado === 'VENCIDO') && (
                          <button onClick={() => onMarcarPagado?.(p?.id)} className="px-2 py-0.5 rounded text-[9px] font-oranienbaum text-black" style={{ background: '#34D399' }}>✓</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
              {Object.keys(pagosByDay)?.length === 0 && <p className="font-oranienbaum text-white/30 text-sm text-center py-8">Sin pagos este mes</p>}
            </div>
          ) : (
            <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="grid grid-cols-7 border-b border-white/7">
                {DAYS_HEADER?.map(d => (
                  <div key={d} className="px-2 py-2 text-center" style={{ borderRight: '1px solid rgba(255,255,255,0.05)' }}>
                    <span className="font-oranienbaum text-white/30 text-[10px] uppercase tracking-wider">{d}</span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {Array.from({ length: startOffset }, (_, i) => (
                  <div key={`e-${i}`} className="min-h-[70px] p-1" style={{ borderRight: '1px solid rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.04)', background: 'rgba(0,0,0,0.15)' }} />
                ))}
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1;
                  const dayPagos = pagosByDay?.[day] || [];
                  const isToday = hoy?.getDate() === day && hoy?.getMonth() === month && hoy?.getFullYear() === year;
                  const isSelected = selectedDay === day;
                  return (
                    <div key={day} onClick={() => setSelectedDay(isSelected ? null : day)}
                      className="min-h-[70px] p-1.5 cursor-pointer transition-colors hover:bg-white/3"
                      style={{ borderRight: '1px solid rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.04)', background: isSelected ? 'rgba(96,165,250,0.06)' : 'transparent' }}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-oranienbaum text-xs font-semibold" style={{ color: isToday ? '#D6EF4E' : 'rgba(255,255,255,0.50)', background: isToday ? 'rgba(214,239,78,0.12)' : 'transparent', borderRadius: 4, padding: isToday ? '1px 5px' : '0' }}>{day}</span>
                        {dayPagos?.length > 0 && <span className="font-oranienbaum text-[9px] text-white/30">{dayPagos?.length}</span>}
                      </div>
                      <div className="space-y-0.5">
                        {dayPagos?.slice(0, 2)?.map((p, pi) => {
                          const total = p?.total || (p?.base_imponible * (1 + (p?.iva_porcentaje || 21) / 100));
                          return (
                            <div key={pi} className="rounded px-1 py-0.5" style={{ background: `${ESTADO_COLORS?.[p?.estado] || '#60A5FA'}18`, borderLeft: `2px solid ${ESTADO_COLORS?.[p?.estado] || '#60A5FA'}` }}>
                              <p className="font-oranienbaum text-[8px] leading-tight truncate" style={{ color: ESTADO_COLORS?.[p?.estado] || '#60A5FA' }}>{p?.descripcion}</p>
                              <p className="font-oranienbaum text-[8px] text-white/50">{fmtEur(total)}</p>
                            </div>
                          );
                        })}
                        {dayPagos?.length > 2 && <p className="font-oranienbaum text-[8px] text-white/30 pl-1">+{dayPagos?.length - 2} más</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {selectedDay && pagosByDay?.[selectedDay]?.length > 0 && (
            <div className="rounded-2xl p-4" style={{ background: 'rgba(96,165,250,0.04)', border: '1px solid rgba(96,165,250,0.15)' }}>
              <h3 className="font-oranienbaum font-semibold text-white/70 text-sm mb-3">Pagos del día {selectedDay}</h3>
              <div className="space-y-2">
                {pagosByDay?.[selectedDay]?.map((p, i) => {
                  const total = p?.total || (p?.base_imponible * (1 + (p?.iva_porcentaje || 21) / 100));
                  return (
                    <div key={i} className="flex items-center justify-between px-3 py-2 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full" style={{ background: ESTADO_COLORS?.[p?.estado] || '#60A5FA' }} />
                        <div>
                          <p className="font-oranienbaum text-white/80 text-xs">{p?.descripcion}</p>
                          <p className="font-oranienbaum text-white/40 text-[10px]">{p?.proveedor || '—'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-oranienbaum text-white/80 text-xs font-semibold">{fmtEur(total)}</span>
                        <span className="px-2 py-0.5 rounded-full text-[9px] font-oranienbaum" style={{ background: `${ESTADO_COLORS?.[p?.estado] || '#60A5FA'}15`, color: ESTADO_COLORS?.[p?.estado] || '#60A5FA' }}>{p?.estado}</span>
                        {(p?.estado === 'PENDIENTE' || p?.estado === 'VENCIDO') && (
                          <button onClick={() => { onMarcarPagado?.(p?.id); loadPagos(); }} className="px-2 py-1 rounded-lg text-[9px] font-oranienbaum font-semibold text-black" style={{ background: '#34D399' }}>Marcar Pagado</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── PREVISIÓN 12 MESES ───────────────────────────────────────────────────────
const Prevision12Meses = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const hoy = new Date();
        const limite = new Date(hoy);
        limite?.setFullYear(hoy?.getFullYear() + 1);
        const pad = (n) => String(n)?.padStart(2, '0');
        const ymd = (d) => `${d?.getFullYear()}-${pad(d?.getMonth() + 1)}-${pad(d?.getDate())}`;
        const { data: rows } = await supabase
          ?.from('fin_gastos')
          ?.select('fecha_vencimiento, total, base_imponible, iva_porcentaje, estado')
          ?.eq('es_prevision', true)
          ?.gte('fecha_vencimiento', ymd(hoy))
          ?.lte('fecha_vencimiento', ymd(limite))
          ?.neq('estado', 'CANCELADO');
        const byMonth = {};
        (rows || [])?.forEach(r => {
          const key = (r?.fecha_vencimiento || '')?.substring(0, 7);
          if (!key) return;
          const total = r?.total || (r?.base_imponible * (1 + (r?.iva_porcentaje || 21) / 100));
          byMonth[key] = (byMonth?.[key] || 0) + (total || 0);
        });
        const meses = [];
        for (let i = 0; i < 12; i++) {
          const d = new Date(hoy?.getFullYear(), hoy?.getMonth() + i, 1);
          const key = `${d?.getFullYear()}-${pad(d?.getMonth() + 1)}`;
          const label = d?.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' })?.toUpperCase();
          meses?.push({ key, label, total: byMonth?.[key] || 0 });
        }
        setData(meses);
      } catch (_) {} finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return null;

  return (
    <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="flex items-center gap-2 mb-4">
        <Icon name="TrendingUp" size={14} className="text-[#60A5FA]" />
        <h3 className="font-oranienbaum font-semibold text-white/70 text-sm">Previsión Próximos 12 Meses</h3>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-2 mb-4">
        {data?.map(m => (
          <div key={m?.key} className="rounded-xl p-2 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <p className="font-oranienbaum text-white/40 text-[9px] mb-1">{m?.label}</p>
            <p className="font-oranienbaum text-white/80 text-[10px] font-semibold">{m?.total > 0 ? fmtEur(m?.total) : '—'}</p>
          </div>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={100}>
        <BarChart data={data} barSize={10}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
          <XAxis dataKey="label" tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 8, fontFamily: 'Oranienbaum' }} axisLine={false} tickLine={false} />
          <YAxis hide />
          <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, fontFamily: 'Oranienbaum', fontSize: 11 }} formatter={v => [fmtEur(v)]} />
          <Bar dataKey="total" fill="#60A5FA" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const FinGastos = () => {
  // State
  const [gastos, setGastos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [kpisLoading, setKpisLoading] = useState(true);
  const [centros, setCentros] = useState([]);
  const [proximosPagos, setProximosPagos] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);

  // Modals
  const [showGastoForm, setShowGastoForm] = useState(false);
  const [editingGasto, setEditingGasto] = useState(null);
  const [showRecurrenteForm, setShowRecurrenteForm] = useState(false);
  const [editingRecurrente, setEditingRecurrente] = useState(null);
  const [showCalendario, setShowCalendario] = useState(false);
  const [showOCR, setShowOCR] = useState(false);
  const [actualizandoRec, setActualizandoRec] = useState(null);
  const [deleteRecModal, setDeleteRecModal] = useState(null);
  const [deleteRecHistorico, setDeleteRecHistorico] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState('Todos');
  const [filterTipo, setFilterTipo] = useState('Todos');
  const [filterRecurrencia, setFilterRecurrencia] = useState('Todos');
  const [filterCategoria, setFilterCategoria] = useState('Todos');
  const [filterCentro, setFilterCentro] = useState('');
  const [filterProveedor, setFilterProveedor] = useState('');
  const [showPrevisiones, setShowPrevisiones] = useState(true);
  const [pageSize, setPageSize] = useState(25);

  // Load data
  const loadAll = useCallback(async () => {
    setLoading(true);
    setKpisLoading(true);
    try {
      const [kpisData, centrosData] = await Promise.all([
        gastosService?.getKPIs({ period: 'month', soloReales: false }),
        centrosCosteService?.getAll(),
      ]);
      setKpis(kpisData);
      setCentros(centrosData);
    } catch (err) { console.error(err); } finally { setKpisLoading(false); }

    try {
      const data = await gastosService?.getAll({ includePrevision: true });
      setGastos(data || []);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  const loadProximosPagos = useCallback(async () => {
    try {
      const hoy = new Date();
      const limite = new Date(hoy);
      limite?.setDate(hoy?.getDate() + 45);
      const pad = (n) => String(n)?.padStart(2, '0');
      const ymd = (d) => `${d?.getFullYear()}-${pad(d?.getMonth() + 1)}-${pad(d?.getDate())}`;
      const { data } = await supabase
        ?.from('fin_gastos')
        ?.select('id, descripcion, proveedor, total, base_imponible, iva_porcentaje, estado, fecha_vencimiento, fecha')
        ?.neq('estado', 'CANCELADO')
        ?.neq('estado', 'PAGADO')
        ?.lte('fecha_vencimiento', ymd(limite))
        ?.order('fecha_vencimiento', { ascending: true })
        ?.limit(8);
      setProximosPagos(data || []);
    } catch (_) {}
  }, []);

  useEffect(() => { loadAll(); loadProximosPagos(); }, [loadAll, loadProximosPagos]);

  // Filtered + sorted gastos
  const filteredGastos = useMemo(() => {
    let rows = gastos;
    if (!showPrevisiones) rows = rows?.filter(g => !g?.es_prevision);
    if (filterEstado !== 'Todos') rows = rows?.filter(g => g?.estado === filterEstado);
    if (filterTipo !== 'Todos') rows = rows?.filter(g => g?.tipo === filterTipo);
    if (filterRecurrencia === 'RECURRENTE') rows = rows?.filter(g => !!g?.recurrente_id);
    if (filterRecurrencia === 'PUNTUAL') rows = rows?.filter(g => !g?.recurrente_id);
    if (filterCategoria !== 'Todos') rows = rows?.filter(g => g?.categoria === filterCategoria);
    if (filterCentro) rows = rows?.filter(g => g?.centro_coste_id === filterCentro);
    if (filterProveedor) rows = rows?.filter(g => g?.proveedor?.toLowerCase()?.includes(filterProveedor?.toLowerCase()));
    if (search) rows = rows?.filter(g => g?.descripcion?.toLowerCase()?.includes(search?.toLowerCase()) || g?.proveedor?.toLowerCase()?.includes(search?.toLowerCase()));
    return smartSort(rows);
  }, [gastos, showPrevisiones, filterEstado, filterTipo, filterRecurrencia, filterCategoria, filterCentro, filterProveedor, search]);

  // Chart data
  const catChartData = useMemo(() => {
    const map = {};
    gastos?.filter(g => !g?.es_prevision)?.forEach(g => { map[g?.categoria] = (map?.[g?.categoria] || 0) + fmt(g?.total); });
    return Object.entries(map)?.sort((a, b) => b?.[1] - a?.[1])?.slice(0, 8)?.map(([cat, val]) => ({ name: cat?.replace(/_/g, ' '), value: val }));
  }, [gastos]);

  const fijoVarData = useMemo(() => [
    { name: 'Fijos', value: gastos?.filter(g => g?.tipo === 'FIJO' && !g?.es_prevision)?.reduce((s, g) => s + fmt(g?.total), 0) },
    { name: 'Variables', value: gastos?.filter(g => g?.tipo === 'VARIABLE' && !g?.es_prevision)?.reduce((s, g) => s + fmt(g?.total), 0) },
  ], [gastos]);

  // Handlers
  const handleSaveGasto = async (formData, baseCalculada, ivaNum, totalNum) => {
    const payload = {
      fecha: formData?.fecha, numero_factura: formData?.numero_factura || null,
      descripcion: formData?.descripcion, proveedor: formData?.proveedor || null,
      proveedor_cif: formData?.proveedor_cif || null, categoria: formData?.categoria,
      tipo: formData?.tipo, departamento: formData?.departamento || null,
      centro_coste_id: formData?.centro_coste_id || null, base_imponible: baseCalculada,
      iva_porcentaje: ivaNum, estado: formData?.estado,
      fecha_vencimiento: formData?.fecha_vencimiento || null, forma_pago: formData?.forma_pago || null,
      observaciones: formData?.observaciones || null,
      es_prevision: editingGasto ? editingGasto?.es_prevision : false,
    };
    if (editingGasto) {
      await gastosService?.update(editingGasto?.id, payload);
    } else if (formData?.recurrencia === 'RECURRENTE') {
      const recPayload = {
        nombre: formData?.descripcion, proveedor: formData?.proveedor || null,
        proveedor_cif: formData?.proveedor_cif || null, categoria: formData?.categoria,
        tipo: formData?.tipo, departamento: formData?.departamento || null,
        centro_coste_id: formData?.centro_coste_id || null, importe_total: totalNum,
        importe_habitual: baseCalculada, iva_porcentaje: ivaNum,
        periodicidad_pago: formData?.periodicidad_pago || 'MENSUAL',
        periodicidad_imputacion: formData?.periodicidad_imputacion || 'MENSUAL',
        periodicidad: formData?.periodicidad_pago || 'MENSUAL',
        dia_habitual_pago: formData?.dia_habitual_pago ? parseInt(formData?.dia_habitual_pago) : 1,
        dia_pago: formData?.dia_habitual_pago ? parseInt(formData?.dia_habitual_pago) : null,
        fecha_inicio: formData?.fecha_inicio || formData?.fecha,
        proximo_vencimiento: formData?.fecha_inicio || formData?.fecha,
        fecha_fin: formData?.fecha_fin || null, activo: true, es_estimado: formData?.es_estimado || false,
      };
      const created = await gastosRecurrentesService?.create(recPayload);
      try { await gastosRecurrentesService?.generarPrevisiones12Meses(created?.id); } catch (_) {}
    } else {
      await gastosService?.create(payload);
    }
    loadAll(); loadProximosPagos();
  };

  const handleSaveRecurrente = async (payload) => {
    if (editingRecurrente) {
      await gastosRecurrentesService?.update(editingRecurrente?.id, payload);
      try { await gastosRecurrentesService?.generarPrevisiones12Meses(editingRecurrente?.id); } catch (_) {}
    } else {
      const created = await gastosRecurrentesService?.create(payload);
      try { await gastosRecurrentesService?.generarPrevisiones12Meses(created?.id); } catch (_) {}
    }
    loadAll(); loadProximosPagos();
  };

  const handleMarcarPagado = async (id) => {
    try {
      await gastosService?.marcarPagado(id, new Date()?.toISOString()?.split('T')?.[0]);
      loadAll(); loadProximosPagos();
    } catch (err) { alert('Error: ' + err?.message); }
  };

  const handleActualizarImporte = async (nuevoTotal, ivaPct, desdeFecha) => {
    await gastosRecurrentesService?.actualizarImporteFuturo(actualizandoRec?.recurrente_id || actualizandoRec?.id, nuevoTotal, ivaPct, desdeFecha);
    loadAll(); loadProximosPagos();
  };

  const handleDeleteRecurrente = async (recId) => {
    const { data: historico } = await supabase?.from('fin_gastos')?.select('id')?.eq('recurrente_id', recId)?.neq('estado', 'PREVISTO')?.limit(1);
    setDeleteRecHistorico((historico || [])?.length > 0);
    setDeleteRecModal({ id: recId });
  };

  const handleDesactivarRecurrente = async () => {
    await supabase?.from('fin_gastos')?.update({ estado: 'CANCELADO', updated_at: new Date()?.toISOString() })?.eq('recurrente_id', deleteRecModal?.id)?.eq('estado', 'PREVISTO');
    await gastosRecurrentesService?.toggleActivo(deleteRecModal?.id, false);
    loadAll(); loadProximosPagos();
  };

  const handleEliminarRecurrente = async () => {
    await supabase?.from('fin_gastos')?.update({ estado: 'CANCELADO', updated_at: new Date()?.toISOString() })?.eq('recurrente_id', deleteRecModal?.id)?.eq('estado', 'PREVISTO');
    await gastosRecurrentesService?.delete(deleteRecModal?.id);
    loadAll(); loadProximosPagos();
  };

  // KPI cards
  const kpiCards = [
    { label: 'Gastos del Mes', value: fmtEur(kpis?.total || 0), icon: 'TrendingDown', color: '#F87171' },
    { label: 'Costes Fijos Norm.', value: fmtEur(kpis?.fijos || 0), icon: 'Repeat', color: '#FBBF24' },
    { label: 'Pagado este Mes', value: fmtEur(kpis?.pagado || 0), icon: 'CheckCircle', color: '#34D399' },
    { label: 'Pendiente', value: fmtEur(kpis?.pendientes || 0), icon: 'Clock', color: '#60A5FA' },
    { label: 'Vencido', value: fmtEur(kpis?.vencidos || 0), icon: 'AlertTriangle', color: '#F87171' },
    { label: 'Próximos 30 días', value: fmtEur(kpis?.totalPrevisiones || 0), icon: 'Calendar', color: '#A78BFA' },
  ];

  const proveedores = useMemo(() => [...new Set(gastos?.map(g => g?.proveedor)?.filter(Boolean))], [gastos]);

  return (
    <div className="min-h-screen px-4 py-6 md:px-8" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      {/* Modals */}
      {(showGastoForm || editingGasto) && (
        <GastoFormModal onClose={() => { setShowGastoForm(false); setEditingGasto(null); }} onSave={handleSaveGasto} centros={centros} gasto={editingGasto} />
      )}
      {(showRecurrenteForm || editingRecurrente) && (
        <RecurrenteFormModal onClose={() => { setShowRecurrenteForm(false); setEditingRecurrente(null); }} onSave={handleSaveRecurrente} centros={centros} recurrente={editingRecurrente} />
      )}
      {showCalendario && <CalendarioModal onClose={() => setShowCalendario(false)} onMarcarPagado={handleMarcarPagado} />}
      {showOCR && <OCRGastosWizard onClose={() => setShowOCR(false)} onConfirmed={() => { setShowOCR(false); loadAll(); loadProximosPagos(); }} />}
      {actualizandoRec && (
        <ActualizarImporteModal recurrente={actualizandoRec} onClose={() => setActualizandoRec(null)} onConfirm={handleActualizarImporte} />
      )}
      {deleteRecModal && (
        <DeleteRecurrenteModal
          recurrente={{ id: deleteRecModal?.id, nombre: 'Recurrente' }}
          tieneHistorico={deleteRecHistorico}
          onClose={() => setDeleteRecModal(null)}
          onDesactivar={handleDesactivarRecurrente}
          onEliminar={handleEliminarRecurrente}
        />
      )}
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-5 text-xs font-oranienbaum text-white/30">
        <Icon name="Home" size={12} />
        <span>/</span>
        <span className="text-[#D6EF4E]/60">Finanzas Corporativas</span>
        <span>/</span>
        <span className="text-white/50">Gastos</span>
      </div>
      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(251,191,36,0.10)', border: '1px solid rgba(251,191,36,0.25)' }}>
            <Icon name="Receipt" size={20} className="text-[#FBBF24]" />
          </div>
          <div>
            <h1 className="font-oranienbaum font-bold text-white text-2xl tracking-wide">Gastos</h1>
            <p className="font-oranienbaum text-white/40 text-xs">Control de gastos, obligaciones y próximos pagos.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => { setEditingGasto(null); setShowGastoForm(true); }}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-oranienbaum font-semibold text-black"
            style={{ background: '#D6EF4E' }}>
            <Icon name="Plus" size={13} />+ Nuevo gasto
          </button>
          <button onClick={() => { setEditingRecurrente(null); setShowRecurrenteForm(true); }}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-oranienbaum font-semibold"
            style={{ background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.30)', color: '#FBBF24' }}>
            <Icon name="Repeat" size={13} />+ Nuevo recurrente
          </button>
          <button onClick={() => setShowCalendario(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-oranienbaum"
            style={{ background: 'rgba(96,165,250,0.10)', border: '1px solid rgba(96,165,250,0.25)', color: '#60A5FA' }}>
            <Icon name="Calendar" size={13} />Calendario
          </button>
          <button onClick={() => setShowOCR(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/50 hover:text-white"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Icon name="ScanLine" size={13} />OCR
          </button>
          <button onClick={loadAll}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-white/40 hover:text-white"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Icon name="RefreshCw" size={13} />
          </button>
        </div>
      </div>
      {/* ── KPIs ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {kpiCards?.map((kpi, i) => (
          <div key={i} className="rounded-2xl p-3 flex flex-col gap-1.5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center justify-between">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${kpi?.color}15`, border: `1px solid ${kpi?.color}25` }}>
                <Icon name={kpi?.icon} size={12} style={{ color: kpi?.color }} />
              </div>
            </div>
            <p className="font-oranienbaum font-bold text-white/80 text-base leading-tight">{kpisLoading ? '...' : kpi?.value}</p>
            <p className="font-oranienbaum text-white/35 text-[10px] leading-tight">{kpi?.label}</p>
          </div>
        ))}
      </div>
      {/* ── PRÓXIMOS PAGOS STRIP ───────────────────────────────────────────── */}
      {proximosPagos?.length > 0 && (
        <div className="mb-6 rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Icon name="Clock" size={14} className="text-[#FBBF24]" />
              <h2 className="font-oranienbaum font-semibold text-white/70 text-sm">Próximos Pagos</h2>
            </div>
            <button onClick={() => setShowCalendario(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-oranienbaum" style={{ background: 'rgba(96,165,250,0.10)', border: '1px solid rgba(96,165,250,0.20)', color: '#60A5FA' }}>
              <Icon name="Calendar" size={11} />Ver calendario completo
            </button>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1">
            {proximosPagos?.map((p, i) => {
              const total = p?.total || (p?.base_imponible * (1 + (p?.iva_porcentaje || 21) / 100));
              const hoy = new Date();
              const d = new Date((p?.fecha_vencimiento || p?.fecha) + 'T00:00:00');
              const diff = Math.ceil((d - hoy) / (1000 * 60 * 60 * 24));
              const urgente = diff <= 3;
              const proximo = diff <= 7;
              return (
                <div key={i} className="flex-shrink-0 rounded-xl p-3 min-w-[130px]"
                  style={{ background: urgente ? 'rgba(248,113,113,0.06)' : proximo ? 'rgba(251,191,36,0.06)' : 'rgba(255,255,255,0.03)', border: `1px solid ${urgente ? 'rgba(248,113,113,0.20)' : proximo ? 'rgba(251,191,36,0.15)' : 'rgba(255,255,255,0.07)'}` }}>
                  <p className="font-oranienbaum text-[10px] font-semibold mb-1" style={{ color: urgente ? '#F87171' : proximo ? '#FBBF24' : '#60A5FA' }}>
                    {fmtDateShort(p?.fecha_vencimiento || p?.fecha)}
                  </p>
                  <p className="font-oranienbaum text-white/80 text-xs font-semibold truncate max-w-[110px]">{p?.descripcion}</p>
                  {p?.proveedor && <p className="font-oranienbaum text-white/35 text-[10px] truncate max-w-[110px]">{p?.proveedor}</p>}
                  <p className="font-oranienbaum text-white/70 text-xs font-bold mt-1">{fmtEur(total)}</p>
                  <span className="px-1.5 py-0.5 rounded text-[8px] font-oranienbaum" style={{ background: `${ESTADO_COLORS?.[p?.estado] || '#60A5FA'}15`, color: ESTADO_COLORS?.[p?.estado] || '#60A5FA' }}>{p?.estado}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* ── FILTROS ─────────────────────────────────────────────────────────── */}
      <div className="mb-4 rounded-2xl p-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="flex flex-wrap gap-2 items-center">
          {/* Search */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl flex-1 min-w-[160px]" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Icon name="Search" size={13} className="text-white/30" />
            <input value={search} onChange={e => setSearch(e?.target?.value)} placeholder="Buscar..." className="bg-transparent text-xs font-oranienbaum text-white/70 placeholder-white/25 outline-none flex-1" />
          </div>
          {/* Estado */}
          <select value={filterEstado} onChange={e => setFilterEstado(e?.target?.value)} className="px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none cursor-pointer" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            {ESTADOS_FILTER?.map(s => <option key={s} value={s} style={{ background: '#111' }}>{s === 'Todos' ? 'Estado: Todos' : s}</option>)}
          </select>
          {/* Tipo */}
          <select value={filterTipo} onChange={e => setFilterTipo(e?.target?.value)} className="px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none cursor-pointer" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            {['Todos', 'FIJO', 'VARIABLE']?.map(s => <option key={s} value={s} style={{ background: '#111' }}>{s === 'Todos' ? 'Tipo: Todos' : s}</option>)}
          </select>
          {/* Recurrencia */}
          <select value={filterRecurrencia} onChange={e => setFilterRecurrencia(e?.target?.value)} className="px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none cursor-pointer" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            {['Todos', 'PUNTUAL', 'RECURRENTE']?.map(s => <option key={s} value={s} style={{ background: '#111' }}>{s === 'Todos' ? 'Recurrencia: Todos' : s}</option>)}
          </select>
          {/* Categoría */}
          <select value={filterCategoria} onChange={e => setFilterCategoria(e?.target?.value)} className="px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none cursor-pointer" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            {CATEGORIAS?.map(c => <option key={c} value={c} style={{ background: '#111' }}>{c === 'Todos' ? 'Categoría: Todas' : c}</option>)}
          </select>
          {/* Centro */}
          <select value={filterCentro} onChange={e => setFilterCentro(e?.target?.value)} className="px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none cursor-pointer" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <option value="" style={{ background: '#111' }}>Centro: Todos</option>
            {centros?.map(c => <option key={c?.id} value={c?.id} style={{ background: '#111' }}>{c?.nombre}</option>)}
          </select>
          {/* Proveedor */}
          <select value={filterProveedor} onChange={e => setFilterProveedor(e?.target?.value)} className="px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none cursor-pointer" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <option value="" style={{ background: '#111' }}>Proveedor: Todos</option>
            {proveedores?.map(p => <option key={p} value={p} style={{ background: '#111' }}>{p}</option>)}
          </select>
          {/* Previsiones toggle */}
          <button onClick={() => setShowPrevisiones(v => !v)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum transition-all"
            style={showPrevisiones ? { background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.25)', color: '#60A5FA' } : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.40)' }}>
            <Icon name="Eye" size={12} />{showPrevisiones ? 'Con previsiones' : 'Sin previsiones'}
          </button>
          {/* Clear filters */}
          {(filterEstado !== 'Todos' || filterTipo !== 'Todos' || filterRecurrencia !== 'Todos' || filterCategoria !== 'Todos' || filterCentro || filterProveedor || search) && (
            <button onClick={() => { setSearch(''); setFilterEstado('Todos'); setFilterTipo('Todos'); setFilterRecurrencia('Todos'); setFilterCategoria('Todos'); setFilterCentro(''); setFilterProveedor(''); }}
              className="px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/40 hover:text-white/70"
              style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.15)' }}>
              <Icon name="X" size={11} className="inline mr-1" />Limpiar
            </button>
          )}
        </div>
      </div>
      {/* ── CENTRO DE CONTROL — TABLA PRINCIPAL ───────────────────────────── */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon name="LayoutList" size={14} className="text-[#D6EF4E]" />
            <h2 className="font-oranienbaum font-semibold text-white/70 text-sm">Centro de Control de Gastos</h2>
            <span className="px-2 py-0.5 rounded-full text-[9px] font-oranienbaum" style={{ background: 'rgba(214,239,78,0.10)', color: '#D6EF4E', border: '1px solid rgba(214,239,78,0.20)' }}>{filteredGastos?.length} registros</span>
          </div>
          <select value={pageSize} onChange={e => setPageSize(Number(e?.target?.value))} className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum text-white/50 outline-none cursor-pointer" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            {PAGE_SIZES?.map(s => <option key={s} value={s} style={{ background: '#111' }}>{s} por página</option>)}
          </select>
        </div>

        <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  <th className="w-8 px-2 py-3" />
                  {['Concepto', 'Proveedor', 'Categoría', 'Tipo', 'Recurrencia', 'Total', 'Próximo Pago', 'Estado', 'Acciones']?.map(col => (
                    <th key={col} className="px-3 py-3 text-left">
                      <span className="font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider">{col}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={10} className="px-4 py-12 text-center"><div className="flex items-center justify-center"><div className="w-5 h-5 rounded-full border-2 border-white/10 border-t-[#D6EF4E] animate-spin" /></div></td></tr>
                ) : filteredGastos?.length === 0 ? (
                  <tr><td colSpan={10} className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <Icon name="Receipt" size={22} className="text-white/20" />
                      <p className="font-oranienbaum text-white/30 text-sm">No hay gastos que coincidan con los filtros</p>
                      <button onClick={() => { setEditingGasto(null); setShowGastoForm(true); }} className="px-4 py-2 rounded-xl text-xs font-oranienbaum font-semibold text-black" style={{ background: '#D6EF4E' }}>Registrar primer gasto</button>
                    </div>
                  </td></tr>
                ) : filteredGastos?.slice(0, pageSize)?.map(g => {
                  const isExpanded = expandedRow === g?.id;
                  const total = g?.total || (g?.base_imponible * (1 + (g?.iva_porcentaje || 21) / 100));
                  const isRecurrente = !!g?.recurrente_id;
                  const hoy = new Date();
                  const vencDate = g?.fecha_vencimiento ? new Date(g?.fecha_vencimiento + 'T00:00:00') : null;
                  const diasVenc = vencDate ? Math.ceil((vencDate - hoy) / (1000 * 60 * 60 * 24)) : null;
                  const alertaVence = diasVenc !== null && diasVenc >= 0 && diasVenc <= 7 && g?.estado !== 'PAGADO' && g?.estado !== 'CANCELADO';
                  const alertaVencido = g?.estado === 'VENCIDO';

                  return (
                    <React.Fragment key={g?.id}>
                      <tr
                        className="border-b border-white/5 hover:bg-white/2 transition-colors cursor-pointer"
                        style={g?.es_prevision ? { opacity: 0.70 } : g?.estado === 'CANCELADO' ? { opacity: 0.40 } : {}}>
                        {/* Expand toggle */}
                        <td className="px-2 py-3" onClick={() => setExpandedRow(isExpanded ? null : g?.id)}>
                          <Icon name={isExpanded ? 'ChevronDown' : 'ChevronRight'} size={12} className="text-white/30" />
                        </td>
                        {/* Concepto */}
                        <td className="px-3 py-3" onClick={() => setExpandedRow(isExpanded ? null : g?.id)}>
                          <div className="flex items-center gap-2">
                            <Icon name={CAT_ICONS?.[g?.categoria] || 'Circle'} size={11} className="text-white/30 flex-shrink-0" />
                            <div>
                              <p className="font-oranienbaum text-white/80 text-xs font-semibold max-w-[150px] truncate">{g?.descripcion}</p>
                              {alertaVencido && <span className="text-[8px] font-oranienbaum text-[#F87171]">● VENCIDO</span>}
                              {alertaVence && !alertaVencido && <span className="text-[8px] font-oranienbaum text-[#FBBF24]">⚠ Vence en {diasVenc}d</span>}
                            </div>
                          </div>
                        </td>
                        {/* Proveedor */}
                        <td className="px-3 py-3" onClick={() => setExpandedRow(isExpanded ? null : g?.id)}>
                          <span className="font-oranienbaum text-white/50 text-xs">{g?.proveedor || '—'}</span>
                        </td>
                        {/* Categoría */}
                        <td className="px-3 py-3" onClick={() => setExpandedRow(isExpanded ? null : g?.id)}>
                          <span className="font-oranienbaum text-white/40 text-[10px]">{g?.categoria?.replace(/_/g, ' ')}</span>
                        </td>
                        {/* Tipo */}
                        <td className="px-3 py-3" onClick={() => setExpandedRow(isExpanded ? null : g?.id)}>
                          <span className="px-2 py-0.5 rounded-full text-[9px] font-oranienbaum" style={{ background: g?.tipo === 'FIJO' ? 'rgba(251,191,36,0.12)' : 'rgba(167,139,250,0.12)', color: g?.tipo === 'FIJO' ? '#FBBF24' : '#A78BFA', border: `1px solid ${g?.tipo === 'FIJO' ? 'rgba(251,191,36,0.25)' : 'rgba(167,139,250,0.25)'}` }}>{g?.tipo}</span>
                        </td>
                        {/* Recurrencia */}
                        <td className="px-3 py-3" onClick={() => setExpandedRow(isExpanded ? null : g?.id)}>
                          {isRecurrente ? (
                            <span className="px-2 py-0.5 rounded-full text-[9px] font-oranienbaum" style={{ background: 'rgba(96,165,250,0.12)', color: '#60A5FA', border: '1px solid rgba(96,165,250,0.25)' }}>↺ RECURRENTE</span>
                          ) : (
                            <span className="font-oranienbaum text-white/25 text-[9px]">PUNTUAL</span>
                          )}
                        </td>
                        {/* Total */}
                        <td className="px-3 py-3 text-right" onClick={() => setExpandedRow(isExpanded ? null : g?.id)}>
                          <span className="font-oranienbaum font-bold text-white/80 text-xs">{fmtEur(total)}</span>
                        </td>
                        {/* Próximo pago */}
                        <td className="px-3 py-3" onClick={() => setExpandedRow(isExpanded ? null : g?.id)}>
                          <span className="font-oranienbaum text-white/50 text-xs">{fmtDate(g?.fecha_vencimiento || g?.fecha)}</span>
                        </td>
                        {/* Estado */}
                        <td className="px-3 py-3" onClick={() => setExpandedRow(isExpanded ? null : g?.id)}>
                          <span className="px-2 py-0.5 rounded-full text-[9px] font-oranienbaum" style={{ background: `${ESTADO_COLORS?.[g?.estado] || '#60A5FA'}15`, color: ESTADO_COLORS?.[g?.estado] || '#60A5FA', border: `1px solid ${ESTADO_COLORS?.[g?.estado] || '#60A5FA'}30` }}>{g?.estado}</span>
                        </td>
                        {/* Acciones */}
                        <td className="px-3 py-3">
                          <RowActionsMenu
                            g={g}
                            onEdit={(gasto) => { setEditingGasto(gasto); setShowGastoForm(true); }}
                            onMarcarPagado={handleMarcarPagado}
                            onRefresh={() => { loadAll(); loadProximosPagos(); }}
                            onVerCalendario={() => setShowCalendario(true)}
                            onActualizarImporte={isRecurrente ? (gasto) => setActualizandoRec(gasto) : null}
                          />
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={10} className="p-0">
                            <RowDetail g={g} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filteredGastos?.length > pageSize && (
            <div className="px-4 py-3 flex items-center justify-between border-t border-white/5">
              <span className="font-oranienbaum text-white/30 text-xs">Mostrando {Math.min(pageSize, filteredGastos?.length)} de {filteredGastos?.length}</span>
              <button onClick={() => setPageSize(p => p + 25)} className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum text-white/50 hover:text-white" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                Cargar más
              </button>
            </div>
          )}
        </div>
      </div>
      {/* ── PREVISIÓN 12 MESES ─────────────────────────────────────────────── */}
      <div className="mb-6">
        <Prevision12Meses />
      </div>
      {/* ── ANÁLISIS / GRÁFICAS ────────────────────────────────────────────── */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.07)' }} />
          <span className="font-oranienbaum text-white/30 text-[10px] uppercase tracking-wider px-3">Análisis</span>
          <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.07)' }} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Por categoría */}
          {catChartData?.length > 0 && (
            <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex items-center gap-2 mb-3"><Icon name="BarChart2" size={14} className="text-[#FBBF24]" /><h3 className="font-oranienbaum font-semibold text-white/60 text-xs">Gastos por Categoría</h3></div>
              <ResponsiveContainer width="100%" height={150}>
                <BarChart data={catChartData} barSize={12}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 8, fontFamily: 'Oranienbaum' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 9, fontFamily: 'Oranienbaum' }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, fontFamily: 'Oranienbaum', fontSize: 11 }} formatter={v => [fmtEur(v)]} />
                  <Bar dataKey="value" radius={[3, 3, 0, 0]}>{catChartData?.map((_, idx) => <Cell key={idx} fill={CHART_COLORS?.[idx % CHART_COLORS?.length]} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {/* Fijos vs Variables */}
          <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center gap-2 mb-3"><Icon name="PieChart" size={14} className="text-[#A78BFA]" /><h3 className="font-oranienbaum font-semibold text-white/60 text-xs">Fijos vs Variables</h3></div>
            <ResponsiveContainer width="100%" height={120}>
              <PieChart>
                <Pie data={fijoVarData} cx="50%" cy="50%" innerRadius={30} outerRadius={50} dataKey="value" paddingAngle={3}>
                  <Cell fill="#FBBF24" /><Cell fill="#A78BFA" />
                </Pie>
                <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, fontFamily: 'Oranienbaum', fontSize: 11 }} formatter={v => [fmtEur(v)]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-4 mt-2">
              {fijoVarData?.map((d, i) => (
                <div key={d?.name} className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: i === 0 ? '#FBBF24' : '#A78BFA' }} />
                  <span className="font-oranienbaum text-white/40 text-[10px]">{d?.name}: {fmtEur(d?.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinGastos;
